import { createContext, useContext, type ReactNode } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { ShieldAlert } from 'lucide-react'
import type { Capability, Me } from '@region13/shared-types'
import { Page } from '../components/Page'
import { Callout } from '../components/Callout'

const MeFnContext = createContext<(() => Promise<Me>) | null>(null)

/** Provides the `me` fetcher to the capability hooks. Wrap the app tree once,
 *  passing the app's own typed `api.me`. Keeps @region13/ui decoupled from any
 *  app's api client. */
export function CapabilityProvider({ meFn, children }: { meFn: () => Promise<Me>; children: ReactNode }) {
  return <MeFnContext.Provider value={meFn}>{children}</MeFnContext.Provider>
}

/** Shared `me` query + capability check. All call sites share the `['me']`
 *  query key so React Query dedupes the underlying fetch. */
export function useCan(): { me: UseQueryResult<Me>; can: (cap: Capability) => boolean } {
  const meFn = useContext(MeFnContext)
  if (!meFn) throw new Error('useCan must be used within a CapabilityProvider')
  const me = useQuery({ queryKey: ['me'], queryFn: meFn, staleTime: 5 * 60_000 })
  return { me, can: (cap: Capability) => me.data?.capabilities.includes(cap) ?? false }
}

/** Client-side capability gate. Renders children only once `me` has loaded and
 *  the user holds `capability`; otherwise a Forbidden panel. The server enforces
 *  the same rule, so this is purely UX. */
export function RequireCapability({ capability, children }: { capability: Capability; children: ReactNode }) {
  const { me, can } = useCan()
  if (!me.data) return null
  if (!can(capability)) {
    return (
      <Page title="Not allowed">
        <Callout tone="warn" icon={ShieldAlert} title="You do not have access to this page.">
          Ask a Region 13 superadmin if you need it.
        </Callout>
      </Page>
    )
  }
  return <>{children}</>
}
